import { InfoPopover } from "@/components/cinch/InfoPopover";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import type { CohortDetailResponse, CohortDetailRow } from "../../api";
import { fmtCur, fmtPct, monthLabel } from "../../format";

/** Big 16-column table — one row per renewal month, forecast on every row,
 *  actual breakdown for past-and-current months. */
export function RenewalCohortDetail({ data }: { data: CohortDetailResponse }) {
  const rows = data.rows || [];
  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <h2 className="text-lg font-semibold">Renewal Cohort Detail</h2>
        <InfoPopover title="Renewal Cohort Detail">
          <p>Each row is the cohort of accounts with anniversaries in that month. Forecast columns (renew / at risk / churn / unknown counts and ARR) populate from CSM picks for every month. Actual columns populate only for past and current months — derived from <code>BI.REVENUE_WATERFALL</code> by comparing each cohort member's MRR in M-1 vs M.</p>
          <p><b>Renewed $</b> caps each account at its starting MRR (so renewals + expansion + contraction + churn sum back to the cohort total).</p>
          <p>The current month is highlighted; months after the latest closed billing period are marked <i>in-progress</i> — actuals are still settling.</p>
        </InfoPopover>
      </div>
      <p className="-mt-2 text-xs text-muted-foreground">
        12 back · current · 12 forward · forecast always · actuals when billing data exists
      </p>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">By renewal month</CardTitle>
        </CardHeader>
        <CardContent className="overflow-auto p-0">
          <table className="w-full min-w-[1200px] text-xs">
            <thead className="text-left uppercase text-muted-foreground">
              <tr>
                <th className="px-2 py-2">Month</th>
                <th className="px-2 py-2 text-right">Accts</th>
                <th className="px-2 py-2 text-right">ARR</th>
                <th className="px-2 py-2 text-right text-success">Renew</th>
                <th className="px-2 py-2 text-right text-warning">At Risk</th>
                <th className="px-2 py-2 text-right text-danger">Churn</th>
                <th className="px-2 py-2 text-right">Unknown</th>
                <th className="px-2 py-2 text-right">% rate</th>
                <th className="px-2 py-2 text-right">Renewal ARR %</th>
                <th className="px-2 py-2 border-l border-border pl-3 text-right">Actual Renew $</th>
                <th className="px-2 py-2 text-right">Actual Renew #</th>
                <th className="px-2 py-2 text-right">Actual Renewal $ rate</th>
                <th className="px-2 py-2 text-right">Contraction $</th>
                <th className="px-2 py-2 text-right">Contraction %</th>
                <th className="px-2 py-2 text-right">Expansion $</th>
                <th className="px-2 py-2 text-right">Expansion %</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <Row key={r.month} r={r} />
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </section>
  );
}

function Row({ r }: { r: CohortDetailRow }) {
  const dim = !r.actuals_available;
  const italic = r.actuals_in_progress ? "italic" : "";
  const rowClass = r.is_current_month
    ? "bg-brand/10"
    : r.is_past
      ? "bg-muted/30"
      : "";
  return (
    <tr className={cn("border-t border-border", rowClass)}>
      <td className={cn("px-2 py-1.5", r.is_current_month && "font-semibold")}>
        {monthLabel(r.month + "-01")}
        {r.is_current_month && <span className="ml-1 text-[10px] text-muted-foreground">(current)</span>}
        {r.actuals_in_progress && !r.is_current_month && (
          <span className="ml-1 text-[10px] text-warning" title="Billing still settling">
            in progress
          </span>
        )}
      </td>
      <td className="px-2 py-1.5 text-right">{r.accts || "—"}</td>
      <td className="px-2 py-1.5 text-right">{r.arr ? fmtCur(r.arr) : "—"}</td>
      <td className="px-2 py-1.5 text-right text-success">
        {r.renew > 0 ? `${r.renew} · ${fmtCur(r.renew_arr)}` : "—"}
      </td>
      <td className="px-2 py-1.5 text-right text-warning">
        {r.at_risk > 0 ? `${r.at_risk} · ${fmtCur(r.at_risk_arr)}` : "—"}
      </td>
      <td className="px-2 py-1.5 text-right text-danger">
        {r.churn > 0 ? `${r.churn} · ${fmtCur(r.churn_arr)}` : "—"}
      </td>
      <td className="px-2 py-1.5 text-right text-muted-foreground">
        {r.unknown > 0 ? `${r.unknown} · ${fmtCur(r.unknown_arr)}` : "—"}
      </td>
      <td className="px-2 py-1.5 text-right">{r.renewal_pct != null ? fmtPct(r.renewal_pct) : "—"}</td>
      <td className="px-2 py-1.5 text-right">{r.renewal_arr_pct != null ? fmtPct(r.renewal_arr_pct) : "—"}</td>
      <td className={cn("px-2 py-1.5 text-right border-l border-border pl-3", italic, dim && "text-muted-foreground")}>
        {r.actuals_available && r.actual_renew_arr != null ? fmtCur(r.actual_renew_arr) : "—"}
      </td>
      <td className={cn("px-2 py-1.5 text-right", italic, dim && "text-muted-foreground")}>
        {r.actuals_available ? (r.actual_renew_count ?? "—") : "—"}
      </td>
      <td className={cn("px-2 py-1.5 text-right", italic, dim && "text-muted-foreground")}>
        {r.actuals_available && r.actual_renewal_arr_pct != null ? fmtPct(r.actual_renewal_arr_pct) : "—"}
      </td>
      <td className={cn("px-2 py-1.5 text-right", italic, r.actual_contraction_arr ? "text-warning" : "text-muted-foreground")}>
        {r.actuals_available && r.actual_contraction_arr != null ? fmtCur(r.actual_contraction_arr) : "—"}
      </td>
      <td className={cn("px-2 py-1.5 text-right", italic, r.actual_contraction_pct ? "text-warning" : "text-muted-foreground")}>
        {r.actuals_available && r.actual_contraction_pct != null ? fmtPct(r.actual_contraction_pct) : "—"}
      </td>
      <td className={cn("px-2 py-1.5 text-right", italic, r.actual_expansion_arr ? "text-success" : "text-muted-foreground")}>
        {r.actuals_available && r.actual_expansion_arr != null ? fmtCur(r.actual_expansion_arr) : "—"}
      </td>
      <td className={cn("px-2 py-1.5 text-right", italic, r.actual_expansion_pct ? "text-success" : "text-muted-foreground")}>
        {r.actuals_available && r.actual_expansion_pct != null ? fmtPct(r.actual_expansion_pct) : "—"}
      </td>
    </tr>
  );
}
